import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { useMediaQuery } from '@common/hooks';
import { formatarDataHora } from '@common/i18n/formatosData';
import {
  Alert,
  AlertDialog,
  Badge,
  Button,
  type DateRange,
  EmptyState,
  PageHeader,
  Pagination,
  Spinner,
  Table,
  type TableSortDirection,
  useToast,
} from '@ntconsult/aluma-react';
import { format, parseISO } from 'date-fns';
import { SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFiltrosUsuarios, usePromover, useRebaixar, useUsuarios } from '../hooks';
import { PERFIS, SIM_NAO, VINCULOS } from '../hooks/useFiltrosUsuarios';
import { POR_PAGINA_OPCOES } from '../services/usuariosService';
import type { FiltrosUsuarios, Usuario } from '../types/Usuario';
import { UsuariosChips } from './UsuariosChips';
import { UsuariosFiltrosDrawer } from './UsuariosFiltrosDrawer';

const paraIso = (d: Date | null) => (d ? format(d, 'yyyy-MM-dd') : '');
const deIso = (s: string) => (s ? parseISO(s) : null);

type Alvo = { usuario: Usuario; promover: boolean };

// Abaixo de `lg` (a barra lateral ainda ocupa 256 px em 768) a tabela fica compacta.
const COMPACTA = '(max-width: 1023px)';

export function UsuariosPage() {
  const compacta = useMediaQuery(COMPACTA);
  const { t } = useTranslation(['usuarios', 'inicio', 'common', 'erros']);
  const { toast } = useToast();
  const { filtros, alterar, limparFiltros, filtrosAtivos } = useFiltrosUsuarios();
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const { data, isLoading, isError } = useUsuarios(filtros);
  const promover = usePromover();
  const rebaixar = useRebaixar();
  const [alvo, setAlvo] = useState<Alvo | null>(null);
  const mutation = alvo?.promover ? promover : rebaixar;

  async function confirmar() {
    if (!alvo) return;
    const { usuario, promover: acao } = alvo;
    try {
      await mutation.mutateAsync(usuario.id);
      toast.success({
        title: t(acao ? 'usuarios:promovido' : 'usuarios:rebaixado', { nome: usuario.nome }),
      });
    } catch (error) {
      toast.danger({ title: t(`erros:${mensagemDeErro(error)}`) });
    } finally {
      setAlvo(null);
    }
  }

  const totalPaginas = Math.max(1, Math.ceil((data?.total ?? 0) / filtros.porPagina));

  const opcoes = (valores: readonly string[], chave: string) =>
    valores.map((v) => ({ value: v, label: t(`usuarios:${chave}.${v}` as never) }));
  const varios = (
    campo: 'perfil' | 'entrou' | 'vinculo',
    label: string,
    chave: string,
    valores: readonly string[],
  ) => (
    <Table.OptionsFilter
      label={label}
      options={opcoes(valores, chave)}
      value={filtros[campo]}
      onValueChange={(v) => alterar({ [campo]: v } as Partial<FiltrosUsuarios>)}
    />
  );
  const ordenacao = (campo: 'nome' | 'email' | 'ultimoLogin') => ({
    sortable: true,
    sortDirection: (filtros.ordenar === campo ? filtros.direcao : 'none') as TableSortDirection,
    onSortChange: (next: TableSortDirection) =>
      alterar(next === 'none' ? { ordenar: '' } : { ordenar: campo, direcao: next }),
  });

  return (
    <div className="flex flex-col gap-8">
      <PageHeader title={t('usuarios:titulo')} description={t('usuarios:descricao')} />

      <section className="flex flex-col gap-4">
        <h2 className="sr-only">{t('usuarios:titulo')}</h2>
        {compacta && (
          <div>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<SlidersHorizontal size={14} />}
              onClick={() => setFiltrosAbertos(true)}
            >
              {t('usuarios:filtros.botao')}
            </Button>
          </div>
        )}
        <UsuariosChips filtros={filtros} onRemover={alterar} onLimpar={limparFiltros} />

        {isLoading && <Spinner label={t('common:loading')} />}
        {isError && <Alert variant="danger" title={t('erros:REDE')} />}
        {data?.itens.length === 0 && (
          <EmptyState title={t(filtrosAtivos ? 'usuarios:semResultado' : 'usuarios:vazio')} />
        )}

        {data && data.itens.length > 0 && (
          <>
            <Table zebra>
              <Table.Head>
                <Table.Row>
                  <Table.Header
                    filter={
                      <Table.TextFilter
                        label={t('usuarios:colunas.nome')}
                        value={filtros.nome}
                        onValueChange={(nome) => alterar({ nome })}
                      />
                    }
                    {...ordenacao('nome')}
                  >
                    {t('usuarios:colunas.nome')}
                  </Table.Header>
                  <Table.Header
                    className="hidden lg:table-cell"
                    filter={
                      <Table.TextFilter
                        label={t('usuarios:colunas.email')}
                        value={filtros.email}
                        onValueChange={(email) => alterar({ email })}
                      />
                    }
                    {...ordenacao('email')}
                  >
                    {t('usuarios:colunas.email')}
                  </Table.Header>
                  <Table.Header
                    className="hidden lg:table-cell"
                    filter={varios(
                      'perfil',
                      t('usuarios:colunas.perfis'),
                      'filtros.perfis',
                      PERFIS,
                    )}
                  >
                    {t('usuarios:colunas.perfis')}
                  </Table.Header>
                  <Table.Header
                    className="hidden xl:table-cell"
                    filter={
                      <>
                        {varios(
                          'entrou',
                          t('usuarios:filtros.entrou.rotulo'),
                          'filtros.entrou',
                          SIM_NAO,
                        )}
                        <Table.DateRangeFilter
                          label={t('usuarios:colunas.ultimoLogin')}
                          value={{
                            from: deIso(filtros.ultimoLoginDe),
                            to: deIso(filtros.ultimoLoginAte),
                          }}
                          onValueChange={(r: DateRange) =>
                            alterar({
                              ultimoLoginDe: paraIso(r.from),
                              ultimoLoginAte: paraIso(r.to),
                            })
                          }
                        />
                      </>
                    }
                    {...ordenacao('ultimoLogin')}
                  >
                    {t('usuarios:colunas.ultimoLogin')}
                  </Table.Header>
                  <Table.Header
                    className="hidden lg:table-cell"
                    filter={varios('vinculo', t('usuarios:colunas.cadastro'), 'vinculo', VINCULOS)}
                  >
                    {t('usuarios:colunas.cadastro')}
                  </Table.Header>
                  {!compacta && <Table.Header>{t('usuarios:colunas.acoes')}</Table.Header>}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {data.itens.map((u) => {
                  const admin = u.perfis.includes('administrador');
                  const situacao = u.vinculo?.situacao ?? 'nao_verificado';
                  const ultimoLogin = u.ultimoLoginEm
                    ? formatarDataHora(u.ultimoLoginEm)
                    : t('usuarios:nuncaEntrou');
                  const perfisBadges = (
                    <div className="flex flex-wrap gap-1">
                      {u.perfis.map((p) => (
                        <Badge key={p} tone={p === 'administrador' ? 'brand' : 'neutral'}>
                          {t(`inicio:perfis.${p}`)}
                        </Badge>
                      ))}
                    </div>
                  );
                  const acao = (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="xl:whitespace-nowrap"
                      onClick={() => setAlvo({ usuario: u, promover: !admin })}
                    >
                      {t(admin ? 'usuarios:acoes.rebaixar' : 'usuarios:acoes.promover')}
                    </Button>
                  );
                  return (
                    <Table.Row key={u.id}>
                      <Table.Cell className="font-medium">
                        {u.nome}
                        {/* Em mobile a coluna E-mail some; o endereço vai para baixo do nome. */}
                        {compacta && (
                          <>
                            <div className="break-all text-xs font-normal text-foreground-muted">
                              {u.email}
                            </div>
                            <div className="text-xs font-normal text-foreground-muted">
                              {t('usuarios:colunas.ultimoLogin')}: {ultimoLogin}
                            </div>
                            <div className="mt-1">{perfisBadges}</div>
                            <div className="mt-2">{acao}</div>
                          </>
                        )}
                      </Table.Cell>
                      <Table.Cell className="hidden lg:table-cell">
                        {!compacta && (
                          <>
                            {u.email}
                            {/* Entre lg e xl a coluna do último acesso não cabe; vem para baixo do e-mail. */}
                            <div className="text-xs text-foreground-muted xl:hidden">
                              {t('usuarios:colunas.ultimoLogin')}: {ultimoLogin}
                            </div>
                          </>
                        )}
                      </Table.Cell>
                      <Table.Cell className="hidden lg:table-cell">
                        {!compacta && perfisBadges}
                      </Table.Cell>
                      <Table.Cell className="hidden whitespace-nowrap xl:table-cell">
                        {!compacta && ultimoLogin}
                      </Table.Cell>
                      <Table.Cell className="hidden lg:table-cell">
                        <Badge
                          tone={
                            situacao === 'vinculado'
                              ? 'success'
                              : situacao === 'nao_verificado'
                                ? 'neutral'
                                : 'warning'
                          }
                        >
                          {t(`usuarios:vinculo.${situacao}`)}
                        </Badge>
                      </Table.Cell>
                      {!compacta && <Table.Cell>{acao}</Table.Cell>}
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
            <Pagination
              page={Math.min(filtros.pagina, totalPaginas)}
              totalPages={totalPaginas}
              onPageChange={(p) => alterar({ pagina: p })}
              pageSize={filtros.porPagina}
              pageSizeOptions={[...POR_PAGINA_OPCOES]}
              onPageSizeChange={(porPagina) => alterar({ porPagina })}
              totalItems={data.total}
              labels={{ pageSize: t('usuarios:porPagina') }}
            />
          </>
        )}
      </section>

      <UsuariosFiltrosDrawer
        open={filtrosAbertos}
        onClose={() => setFiltrosAbertos(false)}
        filtros={filtros}
        alterar={alterar}
        onLimpar={limparFiltros}
      />

      <AlertDialog open={!!alvo} onOpenChange={(o) => !o && setAlvo(null)}>
        <AlertDialog.Content>
          {/* Promover: a pergunta é o título (A01). Remover: título curto e a frase no corpo (A07). */}
          <AlertDialog.Title>
            {alvo &&
              (alvo.promover
                ? t('usuarios:confirmar.promover', { nome: alvo.usuario.nome })
                : t('usuarios:acoes.rebaixar'))}
          </AlertDialog.Title>
          {alvo && !alvo.promover && (
            <AlertDialog.Description>
              {t('usuarios:confirmar.rebaixar', { nome: alvo.usuario.nome })}
            </AlertDialog.Description>
          )}
          <AlertDialog.Footer>
            <AlertDialog.Cancel variant="secondary">{t('common:cancel')}</AlertDialog.Cancel>
            <AlertDialog.Action
              variant={alvo?.promover ? 'primary' : 'danger'}
              loading={mutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                confirmar();
              }}
            >
              {t(alvo?.promover ? 'usuarios:acoes.promover' : 'usuarios:acoes.rebaixar')}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </div>
  );
}
